import { formatWindowOpening } from "@/lib/scheduling/format"
import type { SchedulingChannel, SchedulingContext } from "@/lib/scheduling/types"
import type { ServiceType } from "@/types"
import type { OrderTiming } from "@/types/external/order-request"

/** Channels a delivery/takeout storefront decides over; INDOOR is out of scope. */
const ORDERABLE_CHANNELS: ServiceType[] = ["DELIVERY", "TAKEOUT"]

/** Why the bag is blocked when no channel can take an order (§2A.5). */
export type SchedulingBlockReason = "WINDOW_FULL" | "NO_UPCOMING_WINDOW" | "PAUSED" | "STORE_CLOSED"

export type SchedulingBlock = {
  reason: SchedulingBlockReason
  /** Opening of the next window that has not started yet; null when unknown. */
  nextOpeningAt: string | null
}

/**
 * Everything the storefront needs to decide, in one pass over the context:
 * whether the bag is free, which menu to show, whether the wizard asks for a
 * modality and which channels each modality may use.
 */
export type StorefrontScheduling = {
  /** False while the context has not resolved — callers keep today's behaviour. */
  known: boolean
  bagUnlocked: boolean
  block: SchedulingBlock | null
  banner: string | null
  /** `?window=` for the menu request; null means the catalog of right now. */
  catalogWindow: string | null
  instantChannels: SchedulingChannel[]
  schedulableChannels: SchedulingChannel[]
  timingStepVisible: boolean
  /** Modality applied silently when the wizard does not ask for it. */
  implicitTiming: OrderTiming
  futureWindows: boolean
  /** Tenant zone every slot and window label is rendered in; null when unknown. */
  timezone: string | null
}

const UNKNOWN: StorefrontScheduling = {
  known: false,
  bagUnlocked: true,
  block: null,
  banner: null,
  catalogWindow: null,
  instantChannels: [],
  schedulableChannels: [],
  timingStepVisible: false,
  implicitTiming: "INSTANT",
  futureWindows: false,
  timezone: null,
}

/** A channel takes an order right now, no scheduling involved. */
export function acceptsInstantNow(channel: SchedulingChannel): boolean {
  return channel.openNow && channel.instantAllowed
}

/**
 * A channel takes a scheduled order right now, with at least one free slot.
 *
 * Under `futureWindows` the grid no longer comes from this payload — the slots
 * endpoint owns it (§9.3) — so the permission alone decides, and an empty
 * `slots[]` is no longer a reason to hide the channel.
 */
export function acceptsScheduling(channel: SchedulingChannel, futureWindows = false): boolean {
  if (!channel.schedulingAllowed) return false
  return futureWindows || channel.slots.length > 0
}

function nextOpeningAt(channels: SchedulingChannel[]): string | null {
  let earliest: string | null = null
  for (const channel of channels) {
    const window = channel.targetWindow
    if (!window || window.isCurrent) continue
    if (!earliest || new Date(window.startsAt).getTime() < new Date(earliest).getTime()) {
      earliest = window.startsAt
    }
  }
  return earliest
}

function resolveBlock(channels: SchedulingChannel[]): SchedulingBlock {
  const nextOpening = nextOpeningAt(channels)
  if (channels.some((c) => c.message === "WINDOW_FULL")) {
    return { reason: "WINDOW_FULL", nextOpeningAt: nextOpening }
  }
  if (channels.some((c) => c.closedReason === "PAUSED")) {
    return { reason: "PAUSED", nextOpeningAt: nextOpening }
  }
  if (channels.some((c) => c.message === "NO_UPCOMING_WINDOW")) {
    return { reason: "NO_UPCOMING_WINDOW", nextOpeningAt: null }
  }
  return { reason: "STORE_CLOSED", nextOpeningAt: nextOpening }
}

function resolveBanner(
  instantChannels: SchedulingChannel[],
  schedulableChannels: SchedulingChannel[],
  now: Date,
  timeZone: string,
): string | null {
  if (instantChannels.length > 0 || schedulableChannels.length === 0) return null
  if (schedulableChannels.some((c) => c.openNow)) return null

  const opensAt = nextOpeningAt(schedulableChannels)
  if (!opensAt) return null
  return `Fechada · abre ${formatWindowOpening(opensAt, now, timeZone)} · agende para essa janela`
}

/**
 * Applies the state table of §2A.5 / §2B.2 to a scheduling context. Pure: the
 * hook wraps it, the fixtures test it, and the checkout stays the authority.
 */
export function resolveStorefrontScheduling(
  context: SchedulingContext | null,
  now: Date = new Date(),
): StorefrontScheduling {
  if (!context) return UNKNOWN

  const channels = context.channels.filter((c) => ORDERABLE_CHANNELS.includes(c.serviceType))
  if (channels.length === 0) return UNKNOWN

  const futureWindows = context.featureFlags.futureWindows
  const instantChannels = channels.filter(acceptsInstantNow)
  const schedulableChannels = channels.filter((channel) =>
    acceptsScheduling(channel, futureWindows),
  )
  const bagUnlocked = instantChannels.length > 0 || schedulableChannels.length > 0

  const closedWithScheduling =
    instantChannels.length === 0 && schedulableChannels.some((c) => !c.openNow)

  return {
    known: true,
    bagUnlocked,
    block: bagUnlocked ? null : resolveBlock(channels),
    banner: resolveBanner(instantChannels, schedulableChannels, now, context.timezone),
    catalogWindow: closedWithScheduling ? nextOpeningAt(schedulableChannels) : null,
    instantChannels,
    schedulableChannels,
    timingStepVisible: instantChannels.length > 0 && schedulableChannels.length > 0,
    implicitTiming:
      schedulableChannels.length > 0 && instantChannels.length === 0 ? "SCHEDULED" : "INSTANT",
    futureWindows,
    timezone: context.timezone,
  }
}

/** The context entry for one channel, or null when it is not published. */
export function findChannel(
  context: SchedulingContext | null,
  serviceType: ServiceType,
): SchedulingChannel | null {
  return context?.channels.find((c) => c.serviceType === serviceType) ?? null
}
